import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  LayoutDashboard, 
  Users, 
  Heart, 
  Settings, 
  LogOut, 
  CheckCircle, 
  XCircle, 
  Eye, 
  BarChart3, 
  Trophy,
  Menu,
  X,
  Search,
  Clock,
  UserCheck,
  UserX,
  Trash2,
  TrendingUp,
  Award,
  Loader2
} from 'lucide-react';
import { auth } from '../firebase/config';
import {
  subscribeToAllContestants,
  updateContestantStatus,
  deleteContestantRegistration,
  clearCurrentVotingSeason,
  Contestant,
  sortContestantsByVotes,
} from '../firebase/dbSchema';
import { withAdminAuth } from '../hoc/withAdminAuth';
import Modal from '../components/Modal';
import SafeImage from '../components/SafeImage';
import Seo from '../components/Seo';
import { PAGE_METADATA } from '../seo/pageMetadata';

// --- Components ---

const Sidebar = ({ isOpen, setIsOpen }: { isOpen: boolean, setIsOpen: (v: boolean) => void }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Contestants', path: '/admin/contestants' },
    { icon: Heart, label: 'Voting Metrics', path: '/admin/voting' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
  ];

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/adminlogin');
  };

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-royal-dark/20 backdrop-blur-sm z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      <aside className={`fixed top-0 left-0 h-full bg-royal-dark text-white w-72 z-50 transition-transform duration-300 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-8 flex flex-col h-full">
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-royal-gold rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-royal-dark" />
              </div>
              <span className="font-display font-black text-xl tracking-tighter">Delight Business Empowerment Admin</span>
            </div>
            <button onClick={() => setIsOpen(false)} className="lg:hidden text-white/50 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="flex-1 space-y-2">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-4 px-6 py-4 rounded-2xl transition-all font-black text-sm uppercase tracking-widest ${
                  (item.path === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(item.path))
                    ? 'bg-royal-purple text-white shadow-lg shadow-royal-purple/20'
                    : 'text-white/50 hover:bg-white/5 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="pt-8 border-t border-white/10">
            <button 
              onClick={handleLogout}
              className="flex items-center gap-4 px-6 py-4 rounded-2xl text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all font-black text-sm uppercase tracking-widest w-full"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// --- Dashboard Section ---

const DashboardOverview = () => {
  const [allContestants, setAllContestants] = useState<Contestant[]>([]);
  const [stats, setStats] = useState({ contestants: 0, pending: 0, votes: 0 });
  const [recentContestants, setRecentContestants] = useState<Contestant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToAllContestants(
      (all) => {
        setAllContestants(all);
        setStats({
          contestants: all.length,
          pending: all.filter((c) => c.status === 'pending').length,
          votes: all.reduce((acc, c) => acc + (c.votes || 0), 0),
        });
        setRecentContestants(all.slice(0, 5));
        setError(null);
        setLoading(false);
      },
      () => {
        setError('Failed to load dashboard data.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleDeleteRecentRegistration = async (contestant: Contestant) => {
    if (!contestant.id) {
      return;
    }

    const confirmed = window.confirm(
      `Delete ${contestant.name}'s registration and all votes linked to this contestant?`
    );

    if (!confirmed) {
      return;
    }

    setActionLoading(true);
    setActionMessage(null);
    setActionError(null);

    try {
      const result = await deleteContestantRegistration(contestant.id);
      setActionMessage(
        `${contestant.name} was deleted. Removed ${result.deletedVotes} linked vote record${result.deletedVotes === 1 ? '' : 's'}.`
      );
    } catch (actionError) {
      setActionError('Failed to delete this registration. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearSeason = async () => {
    if (!stats.contestants) {
      return;
    }

    const confirmed = window.confirm(
      'Delete every contestant registration and all vote records for the current season? This cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    setActionLoading(true);
    setActionMessage(null);
    setActionError(null);

    try {
      const result = await clearCurrentVotingSeason();
      setActionMessage(
        `Season cleared. Deleted ${result.deletedContestants} contestant registration${result.deletedContestants === 1 ? '' : 's'} and ${result.deletedVotes} vote record${result.deletedVotes === 1 ? '' : 's'}.`
      );
    } catch (actionError) {
      setActionError('Failed to clear the current voting season. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-royal-purple"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20 bg-red-50 rounded-[3rem] border border-red-100">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-display font-black text-royal-dark">Error</h3>
        <p className="text-slate-500 mt-2">{error}</p>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Contestants', value: stats.contestants, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Pending Verification', value: stats.pending, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Total Votes Cast', value: stats.votes, icon: Heart, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Growth Rate', value: '+12%', icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50' },
  ];

  const leaderboard = sortContestantsByVotes(
    allContestants.filter((contestant) => contestant.status === 'verified')
  ).slice(0, 5);

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, idx) => (
          <div key={idx} className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 flex items-center gap-6">
            <div className={`${stat.bg} ${stat.color} p-4 rounded-2xl`}>
              <stat.icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{stat.label}</p>
              <h3 className="text-3xl font-display font-black text-royal-dark mt-1">{stat.value}</h3>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 bg-white rounded-[3rem] shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-8 border-b border-slate-100 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-xl font-display font-black text-royal-dark">Recent Registrations</h3>
              <p className="text-sm text-slate-500 mt-1">Delete individual entries here or clear the full voting season.</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/admin/contestants" className="text-royal-purple text-xs font-black uppercase tracking-widest hover:underline">
                View All
              </Link>
              <button
                onClick={handleClearSeason}
                disabled={actionLoading || !stats.contestants}
                className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white transition-all hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Clear Season
              </button>
            </div>
          </div>
          {(actionMessage || actionError) && (
            <div className="px-8 pt-6">
              <div
                className={`rounded-2xl border px-5 py-4 text-sm font-medium ${
                  actionError
                    ? 'border-red-100 bg-red-50 text-red-600'
                    : 'border-green-100 bg-green-50 text-green-700'
                }`}
              >
                {actionError || actionMessage}
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Contestant</th>
                  <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                  <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                  <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentContestants.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-8 py-10 text-center text-sm font-medium text-slate-400">
                      No registrations available.
                    </td>
                  </tr>
                ) : (
                  recentContestants.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl overflow-hidden bg-slate-100">
                            <SafeImage src={c.image} alt={c.name} width={1200} height={1500} className="w-full h-full object-cover" />
                          </div>
                          <div>
                            <p className="font-black text-royal-dark text-sm">Miss {c.name}</p>
                            <p className="text-xs text-slate-400">{c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                          c.status === 'verified' ? 'bg-green-100 text-green-700' :
                          c.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-8 py-6 text-xs text-slate-500 font-medium">
                        {c.createdAt?.toDate ? c.createdAt.toDate().toLocaleDateString() : 'Just now'}
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center justify-end gap-2">
                          <Link to="/admin/contestants" className="text-royal-purple hover:text-royal-dark transition-colors">
                            <Eye className="w-5 h-5" />
                          </Link>
                          <button
                            onClick={() => handleDeleteRecentRegistration(c)}
                            disabled={actionLoading}
                            className="text-red-500 transition-colors hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label={`Delete ${c.name}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-[3rem] shadow-sm border border-slate-100 p-8">
          <h3 className="text-xl font-display font-black text-royal-dark mb-8">Voting Leaderboard</h3>
          {leaderboard.length === 0 ? (
            <p className="text-sm font-medium text-slate-400">Votes will appear here as soon as verified contestants receive them.</p>
          ) : (
            <div className="space-y-6">
              {leaderboard.map((c, idx) => (
                <div key={c.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-royal-accent rounded-lg flex items-center justify-center font-black text-royal-purple text-xs">
                      {idx + 1}
                    </div>
                    <p className="font-black text-royal-dark text-sm">Miss {c.name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                    <span className="font-black text-royal-dark text-sm">{c.votes || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Link to="/admin/voting" className="mt-10 w-full bg-royal-accent text-royal-purple py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-royal-purple hover:text-white transition-all flex items-center justify-center gap-2">
            View Full Metrics <BarChart3 className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// --- Contestants Section ---

const ContestantsManager = () => {
  const [contestants, setContestants] = useState<Contestant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Contestant | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToAllContestants((data) => {
      setContestants(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (id: string, status: Contestant['status']) => {
    setActionLoadingId(id);
    setActionMessage(null);
    setActionError(null);

    try {
      await updateContestantStatus(id, status);
      setActionMessage(`Contestant status updated to ${status}.`);
      setSelected((currentContestant) =>
        currentContestant?.id === id ? { ...currentContestant, status } : currentContestant
      );
    } catch (statusError) {
      setActionError('Failed to update contestant status. Please try again.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteContestant = async (contestant: Contestant) => {
    if (!contestant.id) {
      return;
    }

    const confirmed = window.confirm(
      `Delete ${contestant.name}'s registration and all votes linked to this contestant?`
    );

    if (!confirmed) {
      return;
    }

    setActionLoadingId(contestant.id);
    setActionMessage(null);
    setActionError(null);

    try {
      const result = await deleteContestantRegistration(contestant.id);
      setActionMessage(
        `${contestant.name} was deleted. Removed ${result.deletedVotes} linked vote record${result.deletedVotes === 1 ? '' : 's'}.`
      );
      setSelected((currentContestant) =>
        currentContestant?.id === contestant.id ? null : currentContestant
      );
    } catch (deleteError) {
      setActionError('Failed to delete contestant registration. Please try again.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleClearSeason = async () => {
    if (!contestants.length) {
      return;
    }

    const confirmed = window.confirm(
      'Delete every registration and all vote records for the current voting season? This cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    setBulkActionLoading(true);
    setActionMessage(null);
    setActionError(null);

    try {
      const result = await clearCurrentVotingSeason();
      setActionMessage(
        `Season cleared. Deleted ${result.deletedContestants} contestant registration${result.deletedContestants === 1 ? '' : 's'} and ${result.deletedVotes} vote record${result.deletedVotes === 1 ? '' : 's'}.`
      );
      setSelected(null);
    } catch (clearError) {
      setActionError('Failed to clear the current voting season. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const filteredContestants = contestants.filter((contestant) => {
    const queryValue = searchTerm.trim().toLowerCase();

    if (!queryValue) {
      return true;
    }

    return [contestant.name, contestant.email, contestant.phone, contestant.residence, contestant.origin]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(queryValue));
  });

  const pendingCount = contestants.filter((contestant) => contestant.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-royal-purple"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div>
          <h2 className="text-3xl font-display font-black text-royal-dark">Manage Contestants</h2>
          <p className="mt-2 text-sm font-medium text-slate-500">
            {contestants.length} total registrations, {pendingCount} pending review.
          </p>
        </div>
        <div className="flex w-full flex-col gap-4 md:w-auto md:flex-row">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name, email or phone..."
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-royal-purple"
            />
          </div>
          <button
            onClick={handleClearSeason}
            disabled={bulkActionLoading || !contestants.length}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3 text-xs font-black uppercase tracking-widest text-white transition-all hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Clear Current Season
          </button>
        </div>
      </div>

      {(actionMessage || actionError) && (
        <div
          className={`rounded-[2rem] border px-6 py-5 text-sm font-medium ${
            actionError
              ? 'border-red-100 bg-red-50 text-red-600'
              : 'border-green-100 bg-green-50 text-green-700'
          }`}
        >
          {actionError || actionMessage}
        </div>
      )}

      <div className="bg-white rounded-[3rem] shadow-sm border border-slate-100 overflow-hidden">
        {filteredContestants.length === 0 ? (
          <div className="text-center py-10 bg-slate-50 rounded-2xl border border-slate-100">
            <p className="text-sm font-medium text-slate-400">No contestants match your current search.</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View - hidden on md+ */}
            <div className="md:hidden space-y-4 px-4 py-4">
              {filteredContestants.map((c) => {
                const isBusy = actionLoadingId === c.id;

                return (
                  <div key={c.id} className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                    <div className="flex items-start gap-3">
                      <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                        <SafeImage src={c.image} alt={c.name} width={1200} height={1500} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <h4 className="font-black text-royal-dark text-sm truncate">Miss {c.name}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest shrink-0 ${
                            c.status === 'verified' ? 'bg-green-100 text-green-700' :
                            c.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {c.status}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-1">{c.email}</p>
                        <p className="text-xs text-slate-500">{c.phone}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <Heart className="w-3 h-3 text-red-500 fill-red-500" />
                          <span className="text-xs font-black text-royal-dark">{c.votes || 0} votes</span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <button
                        onClick={() => setSelected(c)}
                        disabled={isBusy}
                        className="flex flex-col items-center justify-center bg-royal-accent text-royal-purple py-3 rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-royal-purple hover:text-white transition-all min-h-[48px] disabled:opacity-50"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4 mb-1" />
                        <span>View</span>
                      </button>
                      <button
                        onClick={() => handleStatusChange(c.id!, 'verified')}
                        disabled={isBusy}
                        className="flex flex-col items-center justify-center bg-green-600 text-white py-3 rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-green-700 transition-all min-h-[48px] disabled:opacity-50"
                        title="Verify"
                      >
                        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                        <span>Verify</span>
                      </button>
                      <button
                        onClick={() => handleStatusChange(c.id!, 'rejected')}
                        disabled={isBusy}
                        className="flex flex-col items-center justify-center bg-red-600 text-white py-3 rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-red-700 transition-all min-h-[48px] disabled:opacity-50"
                        title="Reject"
                      >
                        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
                        <span>Reject</span>
                      </button>
                      <button
                        onClick={() => handleDeleteContestant(c)}
                        disabled={isBusy}
                        className="flex flex-col items-center justify-center bg-slate-900 text-white py-3 rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-black transition-all min-h-[48px] disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 mb-1" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View - hidden on mobile */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Contestant</th>
                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact</th>
                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Votes</th>
                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredContestants.map((c) => {
                    const isBusy = actionLoadingId === c.id;
                    return (
                      <tr key={c.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                              <SafeImage src={c.image} alt={c.name} width={1200} height={1500} className="w-full h-full object-cover" />
                            </div>
                            <div>
                              <p className="font-black text-royal-dark text-sm">Miss {c.name}</p>
                              <p className="text-xs text-slate-400 line-clamp-1 max-w-[200px]">
                                {c.residence || c.address || c.bio}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <p className="text-sm font-medium text-slate-600 line-clamp-1">{c.email}</p>
                          <p className="text-xs text-slate-400">{c.phone}</p>
                        </td>
                        <td className="px-6 py-5">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                            c.status === 'verified' ? 'bg-green-100 text-green-700' :
                            c.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2 font-black text-royal-dark">
                            <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                            {c.votes || 0}
                          </div>
                        </td>
                        <td className="px-6 py-5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setSelected(c)}
                              disabled={isBusy}
                              className="flex items-center gap-1.5 px-3 py-2 text-slate-600 hover:text-royal-purple hover:bg-royal-accent rounded-xl transition-all disabled:cursor-not-allowed disabled:opacity-50 font-black text-xs"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View</span>
                            </button>
                            <button
                              onClick={() => handleStatusChange(c.id!, 'verified')}
                              disabled={isBusy}
                              className="flex items-center gap-1.5 px-3 py-2 text-white bg-green-600 hover:bg-green-700 rounded-xl transition-all disabled:cursor-not-allowed disabled:opacity-50 font-black text-xs"
                              title="Verify"
                            >
                              {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                              <span>Verify</span>
                            </button>
                            <button
                              onClick={() => handleStatusChange(c.id!, 'rejected')}
                              disabled={isBusy}
                              className="flex items-center gap-1.5 px-3 py-2 text-white bg-red-600 hover:bg-red-700 rounded-xl transition-all disabled:cursor-not-allowed disabled:opacity-50 font-black text-xs"
                              title="Reject"
                            >
                              {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
                              <span>Reject</span>
                            </button>
                            <button
                              onClick={() => handleDeleteContestant(c)}
                              disabled={isBusy}
                              className="flex items-center gap-1.5 px-3 py-2 text-white bg-slate-900 hover:bg-black rounded-xl transition-all disabled:cursor-not-allowed disabled:opacity-50 font-black text-xs"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="Contestant Details">
        {selected && (
          <div className="space-y-8">
            <div className="aspect-video rounded-[2rem] overflow-hidden shadow-xl">
              <SafeImage src={selected.image} alt={selected.name} width={1200} height={1500} className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-slate-50 p-6 rounded-2xl">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Full Name</p>
                <p className="font-black text-royal-dark">Miss {selected.name}</p>
              </div>
              <div className="bg-slate-50 p-6 rounded-2xl">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Status</p>
                <p className="font-black text-royal-purple uppercase">{selected.status}</p>
              </div>
              <div className="bg-slate-50 p-6 rounded-2xl">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Email</p>
                <p className="font-black text-royal-dark">{selected.email}</p>
              </div>
              <div className="bg-slate-50 p-6 rounded-2xl">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Phone</p>
                <p className="font-black text-royal-dark">{selected.phone}</p>
              </div>
              <div className="bg-slate-50 p-6 rounded-2xl">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">State of Residence</p>
                <p className="font-black text-royal-dark">{selected.residence || selected.address || 'Not provided'}</p>
              </div>
              <div className="bg-slate-50 p-6 rounded-2xl">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">State of Origin</p>
                <p className="font-black text-royal-dark">{selected.origin || 'Not provided'}</p>
              </div>
            </div>
            <div className="bg-slate-50 p-8 rounded-[2rem]">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Biography</p>
              <p className="text-slate-600 leading-relaxed font-medium">{selected.bio}</p>
            </div>
            <div className="flex gap-4">
              <button 
                onClick={() => handleStatusChange(selected.id!, 'verified')}
                className="flex-1 bg-green-600 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-green-700 transition-all flex items-center justify-center gap-2"
              >
                <UserCheck className="w-5 h-5" /> Verify
              </button>
              <button 
                onClick={() => handleStatusChange(selected.id!, 'rejected')}
                className="flex-1 bg-red-600 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-red-700 transition-all flex items-center justify-center gap-2"
              >
                <UserX className="w-5 h-5" /> Reject
              </button>
              <button
                onClick={() => handleDeleteContestant(selected)}
                className="flex-1 bg-slate-900 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-black transition-all flex items-center justify-center gap-2"
              >
                <Trash2 className="w-5 h-5" /> Delete
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// --- Voting Metrics Section ---

const VotingMetrics = () => {
  const [contestants, setContestants] = useState<Contestant[]>([]);

  useEffect(() => {
    const unsubscribe = subscribeToAllContestants((data) => {
      setContestants(sortContestantsByVotes(data.filter((contestant) => contestant.status === 'verified')));
    });

    return () => unsubscribe();
  }, []);

  const totalVotes = contestants.reduce((acc, c) => acc + (c.votes || 0), 0);

  return (
    <div className="space-y-10">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-display font-black text-royal-dark">Voting Analytics</h2>
        <div className="bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3">
          <Heart className="w-5 h-5 text-red-500 fill-red-500" />
          <span className="text-xl font-display font-black text-royal-dark">{totalVotes} Total Votes</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {contestants.map((c, idx) => (
          <div key={c.id} className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 flex flex-col md:flex-row items-center gap-8">
            <div className="flex items-center gap-6 flex-1">
              <div className="w-16 h-16 bg-royal-accent rounded-2xl flex items-center justify-center font-black text-royal-purple text-xl">
                {idx + 1}
              </div>
              <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-md">
                <SafeImage src={c.image} alt={c.name} width={1200} height={1500} className="w-full h-full object-cover" />
              </div>
              <div>
                <h4 className="text-xl font-display font-black text-royal-dark">Miss {c.name}</h4>
                <p className="text-xs text-slate-400 font-black uppercase tracking-widest">{c.status}</p>
              </div>
            </div>
            
            <div className="w-full md:w-1/2 space-y-2">
              <div className="flex justify-between text-xs font-black uppercase tracking-widest">
                <span className="text-slate-400">Vote Percentage</span>
                <span className="text-royal-purple">{totalVotes > 0 ? ((c.votes / totalVotes) * 100).toFixed(1) : 0}%</span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${totalVotes > 0 ? (c.votes / totalVotes) * 100 : 0}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-royal-purple to-royal-gold"
                />
              </div>
            </div>

            <div className="flex items-center gap-4 px-8 py-4 bg-slate-50 rounded-2xl min-w-[150px] justify-center">
              <Heart className="w-6 h-6 text-red-500 fill-red-500" />
              <span className="text-2xl font-display font-black text-royal-dark">{c.votes || 0}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Main Dashboard Component ---

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const getPageTitle = () => {
    if (location.pathname === '/admin') return 'Dashboard Overview';
    if (location.pathname.startsWith('/admin/contestants')) return 'Contestant Management';
    if (location.pathname.startsWith('/admin/voting')) return 'Voting Metrics';
    if (location.pathname.startsWith('/admin/settings')) return 'System Settings';
    return 'Admin Portal';
  };

  return (
    <>
      <Seo {...PAGE_METADATA.adminDashboard} noIndex />
      <div className="min-h-screen bg-slate-50 flex">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

       <main className="flex-1 lg:ml-72 min-h-screen flex flex-col">
          {/* Header - Mobile: menu + avatar only, Desktop: full header with title */}
          <header className="bg-white border-b border-slate-100 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-30">
            <div className="flex items-center gap-3 md:gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-slate-500 hover:bg-slate-50 rounded-xl flex-shrink-0"
              >
                <Menu className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              {/* Desktop title in header, Mobile title in content */}
              <h1 className="hidden md:block text-xl font-display font-black text-royal-dark uppercase tracking-tighter">
                {getPageTitle()}
              </h1>
            </div>

            <div className="flex items-center gap-3 md:gap-6">
              <div className="hidden sm:flex flex-col items-end">
                <p className="text-sm font-black text-royal-dark">Admin User</p>
                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">Super Admin</p>
              </div>
              <div className="w-9 h-9 md:w-10 md:h-12 bg-royal-accent rounded-xl md:rounded-2xl flex items-center justify-center overflow-hidden flex-shrink-0">
                <img
                  src="https://picsum.photos/seed/admin/200"
                  alt="Delight Business Empowerment admin profile avatar"
                  width={200}
                  height={200}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </header>

          {/* Content */}
          <div className="p-4 md:p-8 lg:p-12 flex-1">
            {/* Mobile-only title above content */}
            <div className="md:hidden mb-6">
              <h2 className="text-2xl font-display font-black text-royal-dark uppercase tracking-tighter">
                {getPageTitle()}
              </h2>
            </div>
            <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="contestants" element={<ContestantsManager />} />
            <Route path="voting" element={<VotingMetrics />} />
            <Route path="settings" element={<div className="p-20 text-center text-slate-400 font-medium">Settings module coming soon...</div>} />
          </Routes>
        </div>

        {/* Footer */}
        <footer className="px-12 py-8 border-t border-slate-100 text-center text-slate-400 text-xs font-medium">
          &copy; 2026 Delight Business Empowerment. All rights reserved. Admin Dashboard v1.0
        </footer>
      </main>
      </div>
    </>
  );
};

export default withAdminAuth(AdminDashboard);
